import { useCallback, useEffect, useState } from 'react';
import {
    List, Datagrid, TextField, DateField, BooleanField,
    Edit, SimpleForm, TextInput, Create, NumberInput,
    SelectInput, BooleanInput,
    useRecordContext, NumberField, TopToolbar, ListButton,
    useNotify,
} from 'react-admin';
import {
    Box, Button, Chip, CircularProgress, IconButton,
    List as MuiList, ListItem, ListItemText, TextField as MuiTextField, Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    learningPathService,
    Course as PathCourseOption,
    LearningPathCourse,
} from '../services/learningPathService';

const DifficultyField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const colorMap: Record<string, 'success' | 'warning' | 'error'> = {
        beginner: 'success',
        intermediate: 'warning',
        advanced: 'error',
    };

    const labelMap: Record<string, string> = {
        beginner: 'Einstieg',
        intermediate: 'Vertiefung',
        advanced: 'Spezialisierung',
    };

    return (
        <Chip
            label={labelMap[record.difficulty] || record.difficulty}
            color={colorMap[record.difficulty] || 'default'}
            size="small"
        />
    );
};

const CourseCountField = () => {
    const record = useRecordContext();
    if (!record) return null;

    return <span>{record.courseCount ?? 0} Kurse</span>;
};

export const LearningPathList = () => (
    <List>
        <Datagrid rowClick="edit">
            <TextField source="title" label="Titel" />
            <TextField source="slug" label="Slug" />
            <DifficultyField />
            <CourseCountField />
            <NumberField source="estimatedDuration" label="Dauer (Min.)" />
            <BooleanField source="isActive" label="Aktiv" />
            <DateField source="createdAt" label="Erstellt" />
        </Datagrid>
    </List>
);

const courseIri = (course: PathCourseOption): string => course['@id'] || `/api/courses/${course.id}`;

const byPosition = (rows: LearningPathCourse[]): LearningPathCourse[] =>
    [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

/**
 * Verwaltung "Kurse im Lernpfad" (#408).
 *
 * Schreibt direkt gegen /api/learning_path_courses (POST/PATCH/DELETE) statt
 * ueber den Formular-Submit: `LearningPath::$courses` liegt bewusst NICHT in
 * der Schreibgruppe `path:write` — eine schreibbare Collection mit
 * orphanRemoval wuerde die Verknuepfungen bei jedem Speichern neu anlegen,
 * und die Fortschrittsberechnung joint ueber genau diese Tabelle. Deshalb
 * PATCHt auch das Umsortieren bestehende Zeilen, statt sie neu zu erzeugen.
 */
export const PathCoursesSection = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const pathId: number | undefined = record?.id ? Number(record.id) : undefined;

    const [pathCourses, setPathCourses] = useState<LearningPathCourse[]>([]);
    const [availableCourses, setAvailableCourses] = useState<PathCourseOption[]>([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadData = useCallback(async () => {
        if (!pathId) return;
        setLoading(true);
        try {
            const [rows, courses] = await Promise.all([
                learningPathService.getPathCourses(pathId),
                learningPathService.getAvailableCourses(),
            ]);
            setPathCourses(byPosition(rows));
            setAvailableCourses(courses);
        } catch (error) {
            notify('Kurse im Lernpfad konnten nicht geladen werden', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [pathId, notify]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (!pathId) return null;

    const assignedCourseIds = new Set(pathCourses.map((pc) => pc.course?.id));
    const unassignedCourses = availableCourses.filter((c) => !assignedCourseIds.has(c.id));

    const handleAdd = async () => {
        const course = unassignedCourses.find((c) => String(c.id) === selectedCourseId);
        if (!course || saving) return;
        const nextSortOrder = pathCourses.reduce((max, pc) => Math.max(max, pc.sortOrder + 1), pathCourses.length);
        setSaving(true);
        try {
            const created = await learningPathService.addCourseToPath(pathId, courseIri(course), nextSortOrder);
            setPathCourses((prev) => byPosition([...prev, created]));
            setSelectedCourseId('');
        } catch (error) {
            notify('Kurs konnte nicht zugeordnet werden', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleMove = async (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (saving || target < 0 || target >= pathCourses.length) return;

        const reordered = [...pathCourses];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

        // Nur Zeilen anfassen, deren Position sich aendert — per PATCH auf die
        // bestehende Zeile, damit Fortschrittsdaten an ihr haengen bleiben.
        const updates = reordered
            .map((pc, position) => ({ pc, position }))
            .filter(({ pc, position }) => pc.sortOrder !== position);

        setSaving(true);
        try {
            await Promise.all(updates.map(({ pc, position }) =>
                learningPathService.updatePathCourse(pc.id, { sortOrder: position })
            ));
            setPathCourses(reordered.map((pc, position) => ({ ...pc, sortOrder: position })));
        } catch (error) {
            notify('Reihenfolge konnte nicht gespeichert werden', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (pathCourse: LearningPathCourse) => {
        if (saving) return;
        const title = pathCourse.course?.title || 'diesen Kurs';
        if (!window.confirm(`Kurs "${title}" aus dem Lernpfad entfernen?`)) return;
        setSaving(true);
        try {
            await learningPathService.removeCourseFromPath(pathCourse.id);
            setPathCourses((prev) => prev.filter((pc) => pc.id !== pathCourse.id));
        } catch (error) {
            notify('Kurs konnte nicht entfernt werden', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ width: '100%', mt: 3 }}>
            <Typography variant="h6" gutterBottom>Kurse im Lernpfad</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Änderungen an dieser Liste werden sofort gespeichert. Der Speichern-Button oben
                gilt nur für die Felder des Lernpfads.
            </Typography>
            {loading ? (
                <CircularProgress size={24} />
            ) : (
                <>
                    <MuiList dense disablePadding>
                        {pathCourses.length === 0 && (
                            <ListItem disableGutters>
                                <ListItemText secondary="Noch keine Kurse zugeordnet." />
                            </ListItem>
                        )}
                        {pathCourses.map((pathCourse, index) => (
                            <ListItem key={pathCourse.id} divider disableGutters>
                                <ListItemText primary={`${index + 1}. ${pathCourse.course?.title || ''}`} />
                                <IconButton
                                    size="small"
                                    aria-label={`${pathCourse.course?.title} nach oben`}
                                    disabled={saving || index === 0}
                                    onClick={() => handleMove(index, -1)}
                                >
                                    <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label={`${pathCourse.course?.title} nach unten`}
                                    disabled={saving || index === pathCourses.length - 1}
                                    onClick={() => handleMove(index, 1)}
                                >
                                    <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label={`${pathCourse.course?.title} entfernen`}
                                    disabled={saving}
                                    onClick={() => handleRemove(pathCourse)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItem>
                        ))}
                    </MuiList>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <MuiTextField
                            select
                            SelectProps={{ native: true }}
                            id="path-course-add-select"
                            label="Kurs auswählen"
                            size="small"
                            sx={{ minWidth: 280 }}
                            value={selectedCourseId}
                            onChange={(event) => setSelectedCourseId(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="" />
                            {unassignedCourses.map((c) => (
                                <option key={c.id} value={c.id}>{c.title}</option>
                            ))}
                        </MuiTextField>
                        <Button
                            variant="outlined"
                            disabled={saving || !selectedCourseId}
                            onClick={handleAdd}
                        >
                            Kurs hinzufügen
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export const LearningPathEdit = () => (
    <Edit actions={<TopToolbar><ListButton /></TopToolbar>}>
        <SimpleForm>
            <TextInput source="title" label="Titel" fullWidth required />
            <TextInput source="description" label="Beschreibung" multiline rows={4} fullWidth />
            <TextInput source="thumbnailUrl" label="Thumbnail URL" fullWidth />
            <SelectInput
                source="difficulty"
                label="Schwierigkeitsgrad"
                choices={[
                    { id: 'beginner', name: 'Einstieg' },
                    { id: 'intermediate', name: 'Vertiefung' },
                    { id: 'advanced', name: 'Spezialisierung' },
                ]}
                fullWidth
            />
            <NumberInput source="estimatedDuration" label="Geschätzte Dauer (Minuten)" />
            <BooleanInput source="isActive" label="Aktiv" />
            <PathCoursesSection />
        </SimpleForm>
    </Edit>
);

export const LearningPathCreate = () => (
    <Create actions={<TopToolbar><ListButton /></TopToolbar>}>
        <SimpleForm>
            <TextInput source="title" label="Titel" fullWidth required />
            <TextInput source="description" label="Beschreibung" multiline rows={4} fullWidth />
            <TextInput source="thumbnailUrl" label="Thumbnail URL" fullWidth />
            <SelectInput
                source="difficulty"
                label="Schwierigkeitsgrad"
                choices={[
                    { id: 'beginner', name: 'Einstieg' },
                    { id: 'intermediate', name: 'Vertiefung' },
                    { id: 'advanced', name: 'Spezialisierung' },
                ]}
                defaultValue="beginner"
                fullWidth
            />
            <NumberInput source="estimatedDuration" label="Geschätzte Dauer (Minuten)" />
            <BooleanInput source="isActive" label="Aktiv" defaultValue={true} />
        </SimpleForm>
    </Create>
);
